import React, { useEffect } from "react";
import CodeBlock from "./CodeBlock";
import docStyles from "../../css/Docs/Docs.module.css";
import { Divider } from "@mui/material";
import DocWrapper from "./DocWrapper";
import { Link } from "react-router-dom";

const InvoiceAction = () => {
  useEffect(() => {
    const el = document.getElementById("introduction");
    if (el) {
      el.scrollIntoView();
    }
  }, []);

  const curlSample = `
~~~curl
curl --location --request PUT 'https://test.eqlfin.com/invoice-action/' 
--header 'Firebase-Env: env/core' 
--header 'Content-Type: application/json' 
--header 'Authorization: Basic {{token}}' 
--data '{
    "invoice_number": "SUN/2023/05",
    "invoice_date": 1682899381000,
    "payment_due_date": 1688083381000,
    "tds": 0,
    "subtotal": 900000,
    "cgst_amount": 0,
    "igst_amount": 2500,
    "sgst_amount": 2500,
    "total": 805000,
    "timezone": "Asia/Kolkata",
    "buyer": {
        "gst_number": "BUYER_GST"
    },
    "seller": {
        "gst_number": "SELLER_GST"
    },
    "po_number": "",
    "payment": {
        "utr_number": "UTR",
        "payment_method": "CHEQUE",
        "payment_date": 1683616804000,
        "amount": 905000
    },
    "stage_completed": null,
    "irn": "",
    "narration": "",
    "moratium": 0
}'
~~~
`;

  return (
    <DocWrapper>
      <div className={docStyles.container}>
        <section id="introduction" className={docStyles.section}>
          <div className={docStyles.heading}>Introduction</div>
          <div className={docStyles.para}>
            The Invoice Action API allows buyers to update and add Goods
            Received Notes (GRN) and payments to existing invoices. It also
            supports the creation of invoices if they do not already exist.
          </div>
        </section>

        <br />
        <Divider sx={{ width: "100%" }} />

        <section id="fields" className={docStyles.section}>
          <div className={docStyles.heading}>Important Fields</div>
          <div className={docStyles.para}>
            To create a demand for an existing office, the following fields are
            important:
            <ul
              style={{
                padding: "0px 0px 5px 10px",
                margin: "5px 0px 5px 10px",
              }}
            >
              <li>schedule</li>
              <li>dueDate</li>
              <li>name</li>
              <li>billingDate</li>
              <li>totalAmount</li>
              <li>billingCustomer</li>
              <li>billingBranch</li>
              <li>Office</li>
            </ul>
          </div>
        </section>

        <br />
        <Divider sx={{ width: "100%" }} />

        <section id="request-example" className={docStyles.section}>
          <div className={docStyles.heading}>Request Example</div>
          <div className={docStyles.para}>
            This is a sample curl request
            <CodeBlock codeMarkdown={curlSample} />
          </div>

          <div className={docStyles.heading} id="schema-description">
            Schema Description
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>invoice_number</code>
                </td>
                <td>ADS742-10237439612qq1234</td>
                <td>string</td>
                <td>invoice number </td>
              </tr>
              <tr>
                <td>
                  <code>invoice_date</code>
                </td>
                <td>1660791539574</td>
                <td>number</td>
                <td>date of the invoice created</td>
              </tr>
              <tr>
                <td>
                  <code>payment_due_date</code>
                </td>
                <td>1660791539574</td>
                <td>number</td>
                <td>due date of the invoice </td>
              </tr>
              <tr>
                <td>
                  <code>tds</code>
                </td>
                <td>200</td>
                <td>number</td>
                <td>tds to be applied</td>
              </tr>
              <tr>
                <td>
                  <code>subtotal</code>
                </td>
                <td>1200</td>
                <td>int/float/number</td>
                <td>sub-total of the amount </td>
              </tr>
              <tr>
                <td>
                  <code>cgst_amount</code>
                </td>
                <td>180</td>
                <td>int/float/number</td>
                <td>cgst amount to be applied</td>
              </tr>
              <tr>
                <td>
                  <code>igst_amount</code>
                </td>
                <td>180</td>
                <td>int/float/number</td>
                <td>igst to be applied</td>
              </tr>
              <tr>
                <td>
                  <code>sgst_amount</code>
                </td>
                <td>0</td>
                <td>int/float/number</td>
                <td>sgst to be applied</td>
              </tr>
              <tr>
                <td>
                  <code>total</code>
                </td>
                <td>1560</td>
                <td>int/float/number</td>
                <td>total amount </td>
              </tr>
              <tr>
                <td>
                  <code>timezone</code>
                </td>
                <td>Asia/Kolkata</td>
                <td>string</td>
                <td>timezone </td>
              </tr>
              <tr>
                <td>
                  <code>stage_completed</code>
                </td>
                <td>null</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <code>irn</code>
                </td>
                <td>can be empty string</td>
                <td>string</td>
                <td>invoice referrence number</td>
              </tr>
              <tr>
                <td>
                  <code>narration</code>
                </td>
                <td>can be empty string</td>
                <td>string</td>
                <td>comments added</td>
              </tr>
              <tr>
                <td>
                  <code>moratium</code>
                </td>
                <td>2</td>
                <td>number</td>
                <td>credit days</td>
              </tr>
              <tr>
                <td>
                  <code>po_number</code>
                </td>
                <td>can be empty string</td>
                <td>string</td>
                <td>purchase order number</td>
              </tr>
            </tbody>
          </table>
          <div id="buyer" className={docStyles.headingTwo}>
            buyer
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>gst_number</code>
                </td>
                <td>GST_NUMBER</td>
                <td>string</td>
                <td>gst number of the buyer</td>
              </tr>
            </tbody>
          </table>
          <div id="seller" className={docStyles.headingTwo}>
            seller
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>gst_number</code>
                </td>
                <td>GST_NUMBER</td>
                <td>string</td>
                <td>gst number of the seller</td>
              </tr>
            </tbody>
          </table>
          <div id="grn" className={docStyles.headingTwo}>
            grn
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>grn_no</code>
                </td>
                <td></td>
                <td>string</td>
                <td>goods received notes</td>
              </tr>
              <tr>
                <td>
                  <code>grn_date</code>
                </td>
                <td>234324234</td>
                <td>int/float/number</td>
                <td>date on which the goods were received</td>
              </tr>
              <tr>
                <td>
                  <code>doc_ref</code>
                </td>
                <td></td>
                <td>string</td>
                <td>referrence ID</td>
              </tr>
            </tbody>
          </table>
          <div id="payment" className={docStyles.headingTwo}>
            payment
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>utr_number</code>
                </td>
                <td>UTR</td>
                <td>string</td>
                <td>Unique Transaction Reference number </td>
              </tr>
              <tr>
                <td>
                  <code>payment_method</code>
                </td>
                <td>METHOD</td>
                <td>string</td>
                <td>method of payment</td>
              </tr>
              <tr>
                <td>
                  <code>payment_date</code>
                </td>
                <td>3244234</td>
                <td>int/float/number</td>
                <td>Date of Payement</td>
              </tr>
              <tr>
                <td>
                  <code>amount</code>
                </td>
                <td>2334</td>
                <td>int/float/number</td>
                <td>Amount paid</td>
              </tr>
            </tbody>
          </table>

          <br />
          <Divider sx={{ width: "100%" }} />

          <section id="response" className={docStyles.section}>
            <div className={docStyles.heading}>Response</div>
            <div className={docStyles.para}>
              Upon successful execution of the Create Demand API, the API will
              return a response containing the newly created demand's unique
              identifier.
            </div>

            <div className={docStyles.subHeading}>201 - Created</div>
            <CodeBlock
              codeMarkdown={`
~~~json
{
    "activityId": "wTtWcTBccQDQ8pXRsfLw",
    "message": "",
    "success": true,
    "code": 201
}
~~~
`}
            />
          </section>

          <Divider sx={{ width: "100%" }} />
          <section id="error" className={docStyles.section}>
            <div className={docStyles.heading}>Error Responses</div>

            <div className={docStyles.para}>
              In case of errors during the API call, you may receive one of the
              following error responses:
              <ul
                style={{
                  padding: "0px 0px 5px 10px",
                  margin: "5px 0px 5px 10px",
                }}
              >
                <li>
                  <b>Unauthorized Error: </b> Occurs when the request lacks
                  valid authentication or authorization credentials.
                </li>
                <li>
                  <b> Server Error: </b> Indicates an unexpected error on the
                  server side, such as a database connection issue or an
                  unhandled exception.
                </li>
              </ul>
            </div>

            <div className={docStyles.subHeading}>400 - Bad Request</div>
            <CodeBlock
              codeMarkdown={`
~~~json
{
    "message": "the field of type 'gst-office' must belong to the requesting office only",
    "success": false,
    "code": 400
}
~~~
`}
            />
          </section>
        </section>
      </div>
      <section className={docStyles.tableOfContentSection}>
        <div className={docStyles.tableOfContent}>
          <div className={docStyles.tocHeading}>Table of contents</div>
          <ul className={docStyles.tocList}>
            <li>
              <Link to="#introduction">Introduction</Link>
            </li>
            <li>
              <Link to="#fields">Important Fields</Link>
            </li>
            <li>
              <Link to="#request-example">Request Example</Link>
            </li>
            <li>
              <Link to="#schema-description">Schema Description</Link>
            </li>
            <li>
              <Link to="#response">Response</Link>
            </li>
            <li>
              <Link to="#error">Error Response</Link>
            </li>
          </ul>
        </div>
      </section>
    </DocWrapper>
  );
};

export default InvoiceAction;
